import { useMemo, useState } from 'react';
import { Crown, Trash2, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  fetchAdminCortesias,
  createAdminCortesia,
  revokeAdminCortesia,
  fetchAdminPlans,
} from '../../api/client';
import { searchAdminUsers, type AdminUserResumo } from '../../api/adminUsers';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PageHeader } from '../../components/ui';
import { SearchInput } from '../../components/ui/search-input';
import { Spinner } from '../../components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useNotification } from '../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../lib/errors';

function formatDate(iso: string | null): string {
  if (!iso) return 'Indefinido';
  return new Date(iso).toLocaleDateString('pt-BR');
}

interface CortesiaForm {
  user: AdminUserResumo | null;
  plano_grupo: string;
  motivo: string;
}

const EMPTY_FORM: CortesiaForm = { user: null, plano_grupo: '', motivo: '' };

export default function CortesiaManagementPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CortesiaForm>(EMPTY_FORM);
  const [userSearch, setUserSearch] = useState('');

  const { data: cortesias, isLoading, error } = useQuery({
    queryKey: ['admin', 'cortesias'],
    queryFn: fetchAdminCortesias,
  });

  const { data: plans } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: fetchAdminPlans,
    enabled: dialogOpen,
  });

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['admin', 'users', 'search', userSearch],
    queryFn: () => searchAdminUsers(userSearch),
    enabled: dialogOpen && userSearch.length >= 2 && !form.user,
  });

  const grupoOptions = useMemo(() => {
    const grupos = new Map<string, string>();
    (plans ?? [])
      .filter((p) => p.ativo)
      .forEach((p) => {
        if (!grupos.has(p.grupo)) grupos.set(p.grupo, p.nome);
      });
    return Array.from(grupos.entries()).map(([value, label]) => ({
      value,
      label: `${value} — ${label}`,
      keywords: [label],
    }));
  }, [plans]);

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(EMPTY_FORM);
    setUserSearch('');
  };

  const createMutation = useMutation({
    mutationFn: () => {
      if (!form.user) throw new Error('Selecione um usuário');
      return createAdminCortesia({
        user_id: form.user.id,
        plano_grupo: form.plano_grupo,
        motivo: form.motivo || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cortesias'] });
      showSuccess('Cortesia concedida');
      closeDialog();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeAdminCortesia(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cortesias'] });
      showSuccess('Cortesia revogada');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Gestão de Cortesias" subtitle="Conceda e revogue acesso cortesia a planos superiores." icon={<Crown />} />
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando cortesias" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestão de Cortesias"
        subtitle="Conceda e revogue acesso cortesia a planos superiores."
        icon={<Crown />}
        actions={
          <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
            <DialogTrigger asChild>
              <Button>Conceder Cortesia</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Conceder Cortesia</DialogTitle>
                <DialogDescription>Conceda acesso a um plano superior sem cobrança.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Usuário</Label>
                  {form.user ? (
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <p className="text-sm font-medium">{form.user.nome}</p>
                        <p className="text-xs text-muted-foreground">{form.user.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm({ ...form, user: null })}
                        aria-label="Trocar usuário"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <SearchInput
                        autoFocus
                        value={userSearch}
                        onChange={setUserSearch}
                        placeholder="Buscar por nome ou email..."
                        className="w-full"
                      />
                      {isLoadingUsers && userSearch.length >= 2 && (
                        <div className="flex justify-center py-2">
                          <Spinner size={16} label="Buscando usuários" />
                        </div>
                      )}
                      {!isLoadingUsers && users.length > 0 && (
                        <div className="rounded-md border divide-y max-h-48 overflow-y-auto">
                          {users.map((u) => (
                            <Button
                              key={u.id}
                              type="button"
                              variant="ghost"
                              className="flex w-full items-center justify-between rounded-none p-2.5 text-left h-auto whitespace-normal font-normal"
                              onClick={() => {
                                setForm({ ...form, user: u });
                                setUserSearch('');
                              }}
                            >
                              <div className="min-w-0">
                                <p className="break-words text-sm font-medium">{u.nome}</p>
                                <p className="break-all text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </Button>
                          ))}
                        </div>
                      )}
                      {userSearch.length >= 2 && !isLoadingUsers && users.length === 0 && (
                        <p className="text-sm text-muted-foreground">Nenhum usuário encontrado.</p>
                      )}
                      {userSearch.length < 2 && (
                        <p className="text-xs text-muted-foreground">Digite ao menos 2 caracteres para buscar.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Grupo do Plano</Label>
                  <Combobox
                    value={form.plano_grupo}
                    onValueChange={(value) => setForm({ ...form, plano_grupo: value })}
                    options={grupoOptions}
                    placeholder="Selecione um grupo..."
                    searchPlaceholder="Buscar grupo..."
                    emptyMessage="Nenhum grupo de plano ativo."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Motivo (opcional)</Label>
                  <Input
                    value={form.motivo}
                    onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                    placeholder="Motivo da cortesia"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDialog}>Cancelar</Button>
                <Button
                  onClick={() => createMutation.mutate()}
                  disabled={!form.user || !form.plano_grupo || createMutation.isPending}
                >
                  Conceder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {(cortesias ?? []).length === 0 && !error ? (
        <Card>
          <CardContent className="p-8 text-center text-[var(--color-text-secondary)]">
            Nenhuma cortesia ativa.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(cortesias ?? []).map((c) => (
            <Card key={c.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    {c.user_nome && <span className="font-medium text-sm">{c.user_nome}</span>}
                    <span className="break-words text-sm text-[var(--color-text-secondary)]">
                      {c.user_email ?? c.user_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                    <Badge variant="secondary">{c.plano_grupo}</Badge>
                    {c.motivo && <span>— {c.motivo}</span>}
                  </div>
                  <div className="text-xs text-[var(--color-text-tertiary)]">
                    Expira: {formatDate(c.expira_em)} · Criada: {formatDate(c.created_at)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => revokeMutation.mutate(c.id)}
                  disabled={revokeMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
